import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserPlus, UserCheck, MessageCircle, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { SOCIALS, socialHref, hasSocials } from "@/lib/socials";

export default function Discover() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data.filter((u) => u.id !== user.id));
    setLoading(false);
  }, [user.id]);
  useEffect(() => { load(); }, [load]);

  const toggleFollow = async (id) => {
    const { data } = await api.post(`/users/${id}/follow`);
    setUsers((us) => us.map((u) => {
      if (u.id !== id) return u;
      const followers = data.following ? [...u.followers, user.id] : u.followers.filter((f) => f !== user.id);
      return { ...u, followers };
    }));
  };

  const shown = users.filter((u) => filter === "all" || u.role === filter);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Discover</h1>
          <p className="text-slate-500">Find creators and brands to collaborate with.</p>
        </div>
        <div className="flex gap-2 bg-white rounded-full p-1 border border-slate-100 w-fit">
          {[{ v: "all", l: "All" }, { v: "creator", l: "Creators" }, { v: "brand", l: "Brands" }].map((t) => (
            <button key={t.v} data-testid={`filter-${t.v}`} onClick={() => setFilter(t.v)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${filter === t.v ? "brand-gradient text-white shadow" : "text-slate-500 hover:text-pink-600"}`}>{t.l}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-pink-500" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {shown.map((u, i) => {
            const following = u.followers.includes(user.id);
            return (
              <motion.div key={u.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-5 hover:-translate-y-1 transition-all duration-200" data-testid="discover-card">
                <div className="flex items-center gap-3">
                  <Avatar className="h-14 w-14 ring-2 ring-pink-100 cursor-pointer" onClick={() => nav(`/profile/${u.id}`)}>
                    <AvatarImage src={u.avatar} /><AvatarFallback className="bg-fuchsia-100 text-fuchsia-700 font-bold text-lg">{(u.name||"U")[0]}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 truncate">{u.name}</p>
                    <p className="text-xs text-slate-400">{u.role === "brand" ? u.company : u.category}</p>
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${u.role === "brand" ? "bg-blue-100 text-blue-700" : "bg-pink-100 text-pink-700"}`}>{u.role}</span>
                  </div>
                </div>
                {u.bio && <p className="text-sm text-slate-500 mt-3 line-clamp-2">{u.bio}</p>}
                <p className="text-xs text-slate-400 mt-2">{u.followers.length} followers</p>
                {hasSocials(u.socials) && (
                  <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`discover-socials-${u.id}`}>
                    {SOCIALS.map((s) => {
                      const href = socialHref(s.key, u.socials?.[s.key]);
                      if (!href) return null;
                      const Icon = s.icon;
                      return (
                        <a key={s.key} href={href} target="_blank" rel="noopener noreferrer" title={s.label} onClick={(e) => e.stopPropagation()}
                          data-testid={`discover-social-${s.key}-${u.id}`}
                          className={`h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 transition-all duration-200 ${s.tint}`}>
                          <Icon className="h-4 w-4" strokeWidth={2.2} />
                        </a>
                      );
                    })}
                  </div>
                )}
                <div className="flex gap-2 mt-4">
                  <button data-testid={`follow-btn-${u.id}`} onClick={() => toggleFollow(u.id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-bold transition-all ${following ? "bg-slate-100 text-slate-600" : "brand-gradient text-white shadow-md shadow-pink-500/30"}`}>
                    {following ? <><UserCheck className="h-4 w-4" /> Following</> : <><UserPlus className="h-4 w-4" /> Follow</>}
                  </button>
                  <button data-testid={`message-btn-${u.id}`} onClick={() => nav(`/messages?to=${u.id}`)}
                    className="h-9 w-9 rounded-full bg-slate-100 hover:bg-pink-100 flex items-center justify-center text-slate-600 hover:text-pink-600 transition-all">
                    <MessageCircle className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
